import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ServiceModal = ({ service, onClose }) => {
  const [selectedPricing, setSelectedPricing] = useState('standard');
  const contentRef = useRef(null);

  const sectionRefs = {
    overview: useRef(null),
    casestudy: useRef(null),
    pricing: useRef(null),
    inquiry: useRef(null),
  };

  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Overview', icon: 'Info', ref: sectionRefs.overview },
    { id: 'casestudy', label: 'Case Study', icon: 'FileText', ref: sectionRefs.casestudy },
    { id: 'pricing', label: 'Pricing', icon: 'DollarSign', ref: sectionRefs.pricing },
    { id: 'inquiry', label: 'Get Quote', icon: 'MessageCircle', ref: sectionRefs.inquiry }
  ];

  const pricingTiers = [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for small projects and startups',
      features: ['Basic functionality', 'Standard support', '30-day warranty', 'Email support']
    },
    {
      id: 'standard',
      name: 'Standard',
      description: 'Ideal for growing businesses',
      features: ['Advanced features', 'Priority support', '90-day warranty', 'Phone & email support', 'Basic training']
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Enterprise-grade solutions',
      features: ['All features included', '24/7 dedicated support', '1-year warranty', 'Comprehensive training', 'Custom integrations']
    }
  ];

  const API_BASE_URL = import.meta.env.VITE_API_URL;

  const handleInquiry = async (e) => {
    e.preventDefault();
    const formElement = e.target;
    const formData = new FormData(formElement);

    formData.append('service', service?.id || 'default-service-id');
    formData.append('budget', service?.pricing?.[selectedPricing] || 'Standard Price');
    formData.append('details', formData.get('message') || 'No project details provided');

    try {
      const response = await fetch(`${API_BASE_URL}/contactUs`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      alert(result?.ResponseMessage || 'Thank you! We will contact you soon.');
      onClose();
    } catch (error) {
      alert(`Something went wrong. ${error.message}`);
    }
  };

  const scrollToSection = (id) => {
    const element = sectionRefs[id]?.current;
    if (element && contentRef.current) {
      contentRef.current.scrollTo({
        top: element.offsetTop - contentRef.current.offsetTop,
        behavior: 'smooth',
      });
      setActiveSection(id);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = contentRef.current;
      if (!scrollContainer) return;

      const offset = 80;
      let currentActive = 'overview';

      const scrollTop = scrollContainer.scrollTop;

      sections.forEach(section => {
        const element = section.ref.current;
        if (element && element.offsetTop <= scrollTop + offset) {
          currentActive = section.id;
        }
      });

      setActiveSection(currentActive);
    };

    const scrollContainer = contentRef.current;
    scrollContainer?.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => scrollContainer?.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 p-0">
      <div className="bg-surface w-full h-full flex flex-col overflow-hidden rounded-none shadow-2xl">

        <div className="flex items-center justify-between px-36 mt-12 p-6  border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
              <Icon name={service.icon} size={28} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-text-primary">{service.title}</h2>
              <p className="text-text-secondary">{service.shortDesc}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary nav-transition rounded-md flex items-center gap-2"
          >
            <span style={{ color: "#1e40af" }}>Back</span>
            <Icon name="ArrowRight" size={24} color="#1e40af" />
          </button>

        </div>

        <div className=" border-border   bg-secondary-100 px-36 flex-shrink-0">
          <nav className="flex space-x-6 overflow-x-auto whitespace-nowrap">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`py-4 flex items-center space-x-2 border-b-2 font-medium nav-transition ${activeSection === section.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:border-primary-300'
                  }`}
              >
                <Icon
                  name={section.icon}
                  size={20}
                  color={activeSection === section.id ? 'var(--color-primary)' : 'var(--color-text-secondary)'}
                />
                <span>{section.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* <div ref={contentRef} className="flex-grow overflow-y-auto ml-36 mr-36 p-6 space-y-10"> */}
        <div ref={contentRef} className="flex-grow overflow-y-auto no-scrollbar ml-40 mr-40 p-6 space-y-10">


          {/* OVERVIEW */}
          <div id="overview" ref={sectionRefs.overview} className="space-y-8">
            <h3 className="text-2xl font-heading font-bold text-text-primary border-b pb-2 mb-6">Service Overview</h3>

            <div>
              <h4 className="text-xl font-semibold text-text-primary mb-4">Description</h4>
              <p className="text-text-secondary leading-relaxed mb-6">{service.description}</p>
            </div>

            <div>
              <h4 className="text-xl font-semibold text-text-primary mb-4">Key Benefits</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {service.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" className="mt-1" />
                    <span className="text-text-secondary">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xl font-semibold text-text-primary mb-4 mt-6">Technology Stack</h4>
              <div className="flex flex-wrap gap-3">
                {service.technologies.map((tech, index) => (
                  <span key={index} className="px-4 py-2 bg-primary-50 text-primary rounded-lg font-medium text-sm">
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <div className="pt-4">
              <h4 className="text-xl font-semibold text-text-primary mb-4">Process Steps</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {service.process.map((step, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-secondary-100 rounded-lg">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <span className="text-text-secondary">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CASE STUDY */}
          <div id="casestudy" ref={sectionRefs.casestudy} className="space-y-8 pt-6 border-t border-border">
            <h3 className="text-2xl font-heading font-bold text-text-primary border-b pb-2 mb-6">
              Case Study: {service.caseStudy.title}
            </h3>
            <div>
              <h4 className="text-xl font-semibold text-text-primary mb-3">Challenge</h4>
              <p className="text-text-secondary leading-relaxed">{service.caseStudy.challenge}</p>
            </div>
            <div>
              <h4 className="text-xl font-semibold text-text-primary mb-3">Solution</h4>
              <p className="text-text-secondary leading-relaxed">{service.caseStudy.solution}</p>
            </div>
            <div>
              <h4 className="text-xl font-semibold text-text-primary mb-3">Results</h4>
              <p className="text-text-secondary leading-relaxed">{service.caseStudy.results}</p>
            </div>
          </div>

          {/* PRICING */}
          <div id="pricing" ref={sectionRefs.pricing} className="space-y-8 pt-6 border-t border-border">
            <h3 className="text-2xl font-heading font-bold text-text-primary border-b pb-2 mb-6">Pricing Tiers</h3>
            <p className="text-text-secondary">Choose the package that fits your needs.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {pricingTiers.map((tier) => (
                <div
                  key={tier.id}
                  onClick={() => setSelectedPricing(tier.id)}
                  className={`border rounded-lg p-6 nav-transition cursor-pointer ${selectedPricing === tier.id ? 'border-primary bg-primary-50 ring-2 ring-primary' : 'hover:border-primary-300'
                    }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-text-primary">{tier.name}</h4>
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedPricing === tier.id ? 'border-primary' : 'border-secondary-300'
                      }`}>
                      {selectedPricing === tier.id && <div className="w-2 h-2 bg-primary rounded-full"></div>}
                    </div>
                  </div>
                  <p className="text-text-secondary text-sm mb-4">{tier.description}</p>
                  <div className="text-2xl font-bold text-primary mb-4">{service.pricing[tier.id]}</div>
                  <ul className="space-y-2">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <Icon name="Check" size={16} color="var(--color-success)" />
                        <span className="text-sm text-text-secondary">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* INQUIRY */}
          <div id="inquiry" ref={sectionRefs.inquiry} className="space-y-8 pt-6 border-t border-border">
            <h3 className="text-2xl font-heading font-bold text-text-primary border-b pb-2 mb-6">Get a Quote</h3>
            <p className="text-text-secondary">Tell us about your project.</p>

            <form onSubmit={handleInquiry} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Full Name *</label>
                  <input type="text" name="name" required className="w-full px-4 py-2 border rounded-md focus-ring" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Email *</label>
                  <input type="email" name="email" required className="w-full px-4 py-2 border rounded-md focus-ring" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Company</label>
                  <input type="text" name="company_name" className="w-full px-4 py-2 border rounded-md focus-ring" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Phone</label>
                  <input type="tel" name="phone" className="w-full px-4 py-2 border rounded-md focus-ring" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Pricing Tier</label>
                <select
                  name="pricingTier"
                  value={selectedPricing}
                  onChange={(e) => setSelectedPricing(e.target.value)}
                  className="w-full px-4 py-2 border rounded-md focus-ring"
                >
                  <option value="basic">Basic - {service.pricing.basic}</option>
                  <option value="standard">Standard - {service.pricing.standard}</option>
                  <option value="premium">Premium - {service.pricing.premium}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Project Description *</label>
                <textarea name="message" required rows={4} className="w-full px-4 py-2 border rounded-md focus-ring"></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Timeline</label>
                <select name="timeline" className="w-full px-4 py-2 border rounded-md focus-ring">
                  <option value="">Select timeline</option>
                  <option value="asap">ASAP</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="3-months">Within 3 months</option>
                  <option value="6-months">Within 6 months</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              <div className="flex space-x-4">
                <button type="submit" className="btn-primary flex-1">Send Inquiry</button>
                <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ServiceModal;